import React, { useState } from "react";
import Descriptor from "../../features/rapidstor/entities/Descriptor";
import type UnitType from "../../features/rapidstor/entities/UnitType";
import {
  distributeUnitsToGroups,
  UnitGroup,
} from "../../features/rapidstor/entities/UnitType";
import { Badge, Col, Row } from "react-bootstrap";
import { ReactComponent as IconQuestionMark } from "../../assets/icon-question-mark.svg";
import { ReactComponent as IconExpand } from "../../assets/icon-expand.svg";
import { ReactComponent as IconUnitArea } from "../../assets/icon-unit-area.svg";
import { ReactComponent as IconUnitLevel } from "../../assets/icon-unit-level.svg";
import { ReactComponent as IconUnitGroup } from "../../assets/icon-unit-group.svg";
import UnitTypeModal from "./UnitTypeModal";
import classes from "./UnitType.module.scss";
import { useLoaderData } from "react-router-dom";
import Location from "../../features/rapidstor/entities/Location";
import UnitPrice from "./UnitPrice";
// import store from "../../store";
// import { setUnitData } from "../../store/bookingSlice";

interface Props extends React.HTMLAttributes<HTMLElement> {
  descriptor: Descriptor;
  unitType: UnitType;
  recommended: boolean;
  onBookUnitClicked: (unitType: UnitType) => void;
  onEmailQuoteClicked: () => void;
  sizeLabel?: string;
}

function UnitType({
  onBookUnitClicked,
  onEmailQuoteClicked,
  descriptor,
  unitType,
  recommended,
  sizeLabel,
}: Props) {
  const location = useLoaderData() as Location;
  const unitGroups = distributeUnitsToGroups(
    descriptor.unitTypes,
    location.rapidstor.groundFloor
  );

  const groupLabels: Record<UnitGroup, string> = {
    upper: "Upper",
    ground: "Ground",
    driveup: "Drive Up",
  };

  // Remove groups that don't have a unit type assigned to them.
  // Slight duplication from the Descriptor component.
  // @TODO: extract method to UnitType.ts.
  const validGroups: string[] = Object.entries(unitGroups)
    .filter(([, type]) => type)
    .map(([group]) => groupLabels[group as UnitGroup]);
  validGroups.sort();

  let levels: string[] = descriptor.unitTypes.map((type) => "" + type.unitTypeId);
  levels.sort();
  levels = [...new Set(levels.map((floor) => (floor === "0" ? "G" : floor)))];

  const [showModal, setShowModal] = useState<boolean>(false);
  const modalOnHide = () => setShowModal(false);

  // function goToBookingForm(reservation: boolean) {
  //   store.dispatch(setUnitData({ location, unitType, descriptor, reservation }));
  //   navigate(
  //     reservation
  //       ? `/locations/${location.slug}/reserve`
  //       : `/locations/${location.slug}/book`
  //   );
  // }

  /*
   @TODO: update the Badge element. It always shows 'Recommended' instead of the correct text,
          which will be dependent on the unit's attributes and the result of the questionnaire.
          Definitely extract that out to its own method somewhere.
   */

  return (
    <>
      <div className={classes.unitTypeTopSection}>
        <div className={classes.unitTypeName}>{unitType.typeName}</div>
        {unitType.totalAvailable > 0 ? (
          recommended ? (
            <Badge className={"rounded-pill"}>Recommended</Badge>
          ) : (
            <Badge className={"rounded-pill"} bg={"info"}>
              Other sizes
            </Badge>
          )
        ) : recommended ? (
          <Badge className={"rounded-pill"}>Recommended - Low Availability</Badge>
        ) : (
          <Badge className={"rounded-pill"} bg={"danger"}>
            Low Availability
          </Badge>
        )}
      </div>
      <div className={`${classes.dimensions} mb-2 fw-semibold`}>
        {sizeLabel ?? `${unitType.width}m x ${unitType.length}m`}
      </div>
      {sizeLabel && (
        <div className={classes.unitTypeName} style={{ marginTop: -4 }}>
          {unitType.width}m x {unitType.length}m ({unitType.area}m²)
        </div>
      )}
      <div
        className={`${classes.imagePanel} bg-white text-center position-relative my-2 pt-4`}
        style={{ borderRadius: "4px", cursor: "pointer" }}
        onClick={() => setShowModal(true)}
      >
        <IconExpand className={classes.expand} />
        <img
          src={descriptor.picture}
          className={`${classes.unitImage} mw-100`}
          aria-hidden
          alt={"Computer render of a storage unit"}
        />
        <div
          className={`${classes.moreInfo} pb-2 d-flex justify-content-center align-items-center`}
        >
          <IconQuestionMark /> <span>More unit information</span>
        </div>
      </div>
      <Row>
        <Col xs={6} className={`${classes.basicDetails} d-flex flex-column`}>
          <div>
            <IconUnitArea className={"text-primary me-2"} style={{ width: "12px" }} />
            <span>{unitType.area}m²</span>
          </div>
          <div>
            <IconUnitLevel className={"text-primary me-2"} style={{ width: "12px" }} />
            <span>Level: {unitType.floor === 0 ? "G" : unitType.floor}</span>
          </div>
        </Col>
        <Col xs={6} className={"text-end"}>
          <UnitPrice unitType={unitType} />
          {unitType.totalAvailable > 0 ? (
            <div className={`${classes.unitsAvailable} text-primary`}>
              {unitType.totalAvailable > 1
                ? `${unitType.totalAvailable} units`
                : "Only 1 unit"}{" "}
              available
            </div>
          ) : null}
        </Col>
      </Row>
      <UnitTypeModal
        show={showModal}
        onHide={modalOnHide}
        locationSlug={location.slug}
        onBookUnitClicked={() => onBookUnitClicked(unitType)}
        onEmailQuoteClicked={onEmailQuoteClicked}
        unitType={unitType}
        descriptor={descriptor}
      />
    </>
  );
}

export default UnitType;
